import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/language';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Home,
  Search,
  Users,
  Star,
  FileText,
  Folder,
  Settings,
  Shield,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  Tag,
  X,
  UserX,
  PanelLeftClose,
  PanelLeft,
  Sun,
  Moon,
} from 'lucide-react';
import { useTheme } from '@/components/theme-provider';
import { useAdmin } from '@/contexts/AdminContext';
import { ProfilesListDialog } from '@/components/sidebar/ProfilesListDialog';
import { SidebarContextualActions } from '@/components/sidebar/SidebarContextualActions';
import { FeedbackModal } from '@/components/feedback/FeedbackModal';

interface SidebarProps {
  className?: string;
}

export const Sidebar = ({ className }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { isAdmin } = useAdmin();
  const { theme, setTheme } = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showProfilesList, setShowProfilesList] = useState(false);
  const [showSuspendedProfilesList, setShowSuspendedProfilesList] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Don't render sidebar if user is not authenticated
  if (!user) return null;

  // Save collapse state and dispatch event when it changes
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(isCollapsed));
    // Dispatch custom event so main content can adjust
    window.dispatchEvent(new CustomEvent('sidebar-collapsed', { detail: { isCollapsed } }));
  }, [isCollapsed]);

  const mainNavItems = [
    { path: '/tableau-de-bord', label: t('navigation.dashboard'), icon: Home },
    { path: '/recherche', label: t('navigation.dispositifs'), icon: Search },
  ];

  const shortcutItems = [
    { path: '/favoris', label: t('common.favorites'), icon: Star },
    { path: '/candidatures', label: t('common.applications'), icon: FileText },
    { path: '/documents', label: t('common.documents'), icon: Folder },
    { path: '/categories', label: t('common.categories'), icon: Tag },
  ];

  const bottomItems = [
    { path: '/settings', label: t('navigation.settings'), icon: Settings },
    ...(isAdmin ? [{ path: '/admin', label: t('common.admin'), icon: Shield }] : []),
  ];

  const isActive = (path: string) => location.pathname === path;

  // Close mobile menu when navigating
  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  // Expose mobile toggle to window for Navbar to use
  useEffect(() => {
    (window as any).toggleMobileSidebar = () => setIsMobileOpen(prev => !prev);
    return () => {
      delete (window as any).toggleMobileSidebar;
    };
  }, []);

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-14 h-[calc(100vh-56px)] bg-card border-r border-border transition-all duration-300 z-40 flex flex-col',
          // Desktop: always visible, collapsible
          'hidden lg:flex',
          isCollapsed ? 'lg:w-16' : 'lg:w-60',
          // Mobile: overlay from left when open
          isMobileOpen && 'flex w-64',
          className
        )}
      >
      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2">
        {/* Close Button - Mobile Only */}
        <div className="lg:hidden mb-4 px-2 flex justify-end">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileOpen(false)}
            className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-secondary"
            aria-label="Fermer le menu"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Primary Actions */}
        {!isCollapsed && (
          <div className="mb-4 px-2">
            <Link
              to="/profile/new"
              onClick={() => setIsMobileOpen(false)}
              className="w-full flex items-center justify-start gap-2 h-10 px-4 rounded-md bg-gradient-to-r from-[#667eea] to-[#764ba2] hover:shadow-[0_4px_15px_rgba(102,126,234,0.4)] text-white font-medium transition-all"
            >
              <Plus className="h-4 w-4" />
              {t('common.newProfile')}
            </Link>
          </div>
        )}

        {/* Collapsed state: Show plus button */}
        {isCollapsed && (
          <div className="mb-4 flex justify-center">
            <Link
              to="/profile/new"
              onClick={() => setIsMobileOpen(false)}
              className="h-10 w-10 flex items-center justify-center rounded-md bg-gradient-to-r from-[#667eea] to-[#764ba2] hover:shadow-[0_4px_15px_rgba(102,126,234,0.4)] text-white transition-all"
              title={t('common.newProfile')}
            >
              <Plus className="h-5 w-5" />
            </Link>
          </div>
        )}

        {/* Main Nav Items */}
        <div className="space-y-1 mb-6">
          {mainNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileOpen(false)}
                className={cn(
                  'w-full flex items-center gap-3 h-10 px-3 rounded-md transition-all font-medium',
                  active
                    ? 'bg-secondary text-primary border-l-2 border-primary'
                    : 'text-foreground hover:bg-secondary hover:text-primary',
                  isCollapsed ? 'justify-center px-0' : 'justify-start'
                )}
              >
                <Icon className="h-5 w-5" />
                {!isCollapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </div>

        {/* Divider */}
        {!isCollapsed && <div className="border-t border-border mb-6" />}

        {/* Mes Profils - Opens Dialog */}
        {!isCollapsed && (
          <div className="mb-6 space-y-1">
            <button
              onClick={() => setShowProfilesList(true)}
              className={cn(
                'w-full flex items-center justify-start gap-3 h-10 px-3 rounded-md transition-all',
                'text-foreground hover:bg-secondary hover:text-primary'
              )}
            >
              <Users className="h-5 w-5" />
              <span className="flex-1 text-left">{t('common.yourProfiles')}</span>
            </button>

            <button
              onClick={() => setShowSuspendedProfilesList(true)}
              className={cn(
                'w-full flex items-center justify-start gap-3 h-10 px-3 rounded-md transition-all',
                'text-foreground hover:bg-secondary hover:text-primary'
              )}
            >
              <UserX className="h-5 w-5" />
              <span className="flex-1 text-left">{t('common.yourSuspendedProfiles')}</span>
            </button>
          </div>
        )}

        {/* Contextual Actions - Dynamic based on current page */}
        <SidebarContextualActions
          isCollapsed={isCollapsed}
          onMobileClose={() => setIsMobileOpen(false)}
        />

        {/* Shortcuts Section - Hidden: Favoris, Candidatures, Documents, Catégories */}
        {/* {!isCollapsed && (
          <div className="mb-4">
            <div className="px-3 mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {t('navigation.shortcuts')}
              </span>
            </div>
            <div className="space-y-1">
              {shortcutItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <Button
                    key={item.path}
                    variant="ghost"
                    onClick={() => handleNavigation(item.path)}
                    className={cn(
                      'w-full justify-start gap-3 h-10 px-3 transition-all',
                      active
                        ? 'bg-card text-foreground font-semibold shadow-sm hover:bg-card'
                        : 'text-muted-foreground hover:bg-card/60 hover:text-foreground'
                    )}
                  >
                    <Icon className={cn('h-4 w-4', active && 'text-foreground')} />
                    <span className="text-sm">{item.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        )} */}
      </nav>

      {/* Help & Collapse Toggle - Above bottom section */}
      <div className={cn(
        "px-3 pb-2 flex items-center gap-1",
        isCollapsed ? "flex-col" : "justify-between"
      )}>
        {/* Help/Support Button */}
        <button
          onClick={() => setShowFeedbackModal(true)}
          className={cn(
            "flex items-center gap-2 h-8 px-2 rounded-md text-muted-foreground hover:text-primary hover:bg-secondary transition-all",
            isCollapsed && "justify-center px-0 w-full"
          )}
          title={t('common.needHelp')}
        >
          <HelpCircle className="h-4 w-4" />
          {!isCollapsed && <span className="text-sm">{t('common.needHelp')}</span>}
        </button>

        {/* Collapse Toggle - Desktop Only */}
        <Button
          variant="ghost"
          size="sm"
          className="hidden lg:flex h-8 w-8 p-0 text-muted-foreground hover:text-primary hover:bg-secondary"
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? "Étendre la barre latérale" : "Réduire la barre latérale"}
          title={isCollapsed ? "Étendre" : "Réduire"}
        >
          {isCollapsed ? (
            <PanelLeft className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Bottom Section */}
      <div className="border-t border-border p-2 space-y-1">
        {/* Settings row with theme toggle */}
        <div className={cn(
          'flex items-center gap-1',
          isCollapsed ? 'flex-col' : 'flex-row'
        )}>
          <Link
            to="/settings"
            onClick={() => setIsMobileOpen(false)}
            className={cn(
              'flex items-center gap-3 h-10 px-3 rounded-md transition-all',
              isActive('/settings')
                ? 'bg-secondary text-primary font-medium'
                : 'text-foreground hover:bg-secondary hover:text-primary',
              isCollapsed ? 'justify-center px-0 w-full' : 'justify-start flex-1'
            )}
          >
            <Settings className="h-5 w-5" />
            {!isCollapsed && <span>{t('navigation.settings')}</span>}
          </Link>

          {/* Theme Toggle Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className={cn(
              'h-10 w-10 p-0 text-muted-foreground hover:text-primary hover:bg-secondary flex-shrink-0',
              isCollapsed && 'w-full'
            )}
            title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
          >
            {theme === 'dark' ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>
        </div>

        {/* Admin button (if admin) */}
        {isAdmin && (
          <Link
            to="/admin"
            onClick={() => setIsMobileOpen(false)}
            className={cn(
              'w-full flex items-center gap-3 h-10 px-3 rounded-md transition-all',
              isActive('/admin')
                ? 'bg-secondary text-primary font-medium'
                : 'text-foreground hover:bg-secondary hover:text-primary',
              isCollapsed ? 'justify-center px-0' : 'justify-start'
            )}
          >
            <Shield className="h-5 w-5" />
            {!isCollapsed && <span>{t('common.admin')}</span>}
          </Link>
        )}
      </div>

      {/* Profiles List Dialog */}
      <ProfilesListDialog
        open={showProfilesList}
        onOpenChange={setShowProfilesList}
        onCreateNew={() => {
          setShowProfilesList(false);
          navigate('/profile/new');
        }}
      />

      {/* Suspended Profiles List Dialog */}
      <ProfilesListDialog
        open={showSuspendedProfilesList}
        onOpenChange={setShowSuspendedProfilesList}
        onCreateNew={() => {
          setShowSuspendedProfilesList(false);
          navigate('/profile/new');
        }}
        suspendedOnly={true}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        open={showFeedbackModal}
        onOpenChange={setShowFeedbackModal}
      />
    </aside>
    </>
  );
};
